import React, { useState, useRef, useCallback, useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import ReusableModal from '../../../../components/ReusableComponents/ReusableModal';
import { Icon, Layout, message, Tooltip, Spin } from 'antd';
import { ApprovalContainer, Content, Sider, ButtonContainer } from '../../styles';
import ReusableDynamicCollapse from '../../../../components/ReusableComponents/ReusableDynamicCollapse';
import { dummyCallback, PrintingModal } from '../../../../utils/complexDocumentConstants';
import SearchableTable from './SearchableTable';
import ActionButton from '../../../../components/ActionButton';
import { APPROVAL_DASHBOARD_MODAL as trans, STATUS_MONITORING } from '../TranslationValues';
import BatchRejectionModal from '../Component/BatchRejection';
import { ApprovalModal, JobProcessingModal } from '../../../../utils/complexDocumentConstants';
import DocumentPanel from './DocumentPanel';
import EmailPanel from './EmailPanel';
import { getChannelStats, handleApproval, sendForPrinting } from '../../../../utils/services/statusMonitoringAPI';
import Timeline from './Timeline';
import { batchAuditData, getJobStatus, metaSearchKeyMappings } from '../../../../utils/constants/statusMonitoring';
import { useSessionContext } from '../../../../context/sessionContext';
import { useSubscription } from '@apollo/client';
import { FETCH_BATCH_AUDIT } from '../../gql/FetchBatches';
import { aggregateBatchStats, getChannelTotals, isShortMessageEnabled } from '../../../../utils/helper';

const NAMESPACES = ['APPROVAL_DASHBOARD_MODAL'];
const channelStatsPayload = {status: 'processing_complete'}
const STATIC_STATS_KEYS = ['uniq_id', 'uniq_name', 'language',];
const panelData = ({
  t,
  activeItem,
  contentType,
  setStatsFilter,
  statsFilter,
  documentChartRef,
  documentBarChartRef,
  emailChartRef,
  totalStats,
  languageStats,
  statsKeys,
  staticStatsKeys,
  setRemovedRecord,
  setFailureRecord,
  setExclusionHistory,
  selectedSearchFilters,
  emailBarChartRef,
  sync,
  setSync,
}) => {
  const panels = [
    {
      key: 'Documents',
      header: t(STATUS_MONITORING.CHANNEL_DOCUMENT.key, STATUS_MONITORING.CHANNEL_DOCUMENT.value),
      content: (
        <DocumentPanel
          jobId={activeItem?.id}
          activeItem={activeItem}
          contentType={contentType}
          statsFilter={statsFilter}
          setStatsFilter={setStatsFilter}
          chartRef={documentChartRef}
          barChartRef={documentBarChartRef}
          totalStats={totalStats}
          languageStats={languageStats}
          statsKeys={statsKeys}
          staticStatsKeys={staticStatsKeys}
          setRemovedRecord={setRemovedRecord}
          setFailureRecord={setFailureRecord}
          setExclusionHistory={setExclusionHistory}
          selectedSearchFilters={selectedSearchFilters}
          reloadSync={sync}
          setReloadSync={setSync}
        />
      ),
      style: { border: 0 },
    },
  ];
  if (contentType !== PrintingModal) {
    panels.push(
      {
        key: 'Email',
        header: t(STATUS_MONITORING.CHANNEL_EMAIL.key, STATUS_MONITORING.CHANNEL_EMAIL.value),
        content: (
          <EmailPanel
          jobId={activeItem?.id}
            activeItem={activeItem}
            contentType={contentType}
            statsFilter={statsFilter}
            setStatsFilter={setStatsFilter}
            chartRef={emailChartRef}
            emailBarChartRef={emailBarChartRef}
            totalStats={totalStats}
            languageStats={languageStats}
            statsKeys={statsKeys}
            staticStatsKeys={staticStatsKeys}
            setRemovedRecord={setRemovedRecord}
            setExclusionHistory={setExclusionHistory}
            selectedSearchFilters={selectedSearchFilters}
          />
        ),
        style: { border: 0 },
      })
    if(isShortMessageEnabled) {
      panels.push({
        key: 'Messages',
        header: t(STATUS_MONITORING.CHANNEL_MESSAGES.key, STATUS_MONITORING.CHANNEL_MESSAGES.value),
        content: <></>,
        style: { border: 0 },
      },
    )}
  }
  return panels;
};

const ApprovalDashboardModal = ({
  visible = false,
  onCancel = dummyCallback,
  title = 'Modal Title',
  contentType,
  t,
  activeItem,
  jobId,
  users,
  subscribed,
}) => {
  const [rejectionModal, setRejectionModal] = useState(false);
  const [statsFilter, setStatsFilter] = useState([]);
  const [loading, setLoading] = useState(false);
  const [auditData, setAuditData] = useState([]);
  const documentChartRef = useRef(null);
  const emailChartRef = useRef(null);
  const emailBarChartRef = useRef(null);
  const documentBarChartRef = useRef(null);
  const buttonContainerRef = useRef(null);
  const [activeChannel, setActiveChannel] = useState('Documents');
  const [totalStats, setTotalStats] = useState({success:0, inprogress:0, failure:0});
  const keyNames = metaSearchKeyMappings(t);
  const [languageStats, setLanguageStats] = useState([]);
  const [sync, setSync] = useState(true);
  const [timelineLoader, setTimelineLoader] = useState(false);
  const [renderSearchTable, setRenderSearchTable] = useState(true);
  const [removedRecord, setRemovedRecord] = useState(false);
  const [failureRecord, setFailureRecord] = useState(false);
  const [exclusionHistory, setExclusionHistory] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedAllRows, setSelectedAllRows] = useState(true);
  const [selectedSearchFilters, setSelectedSearchFilters] = useState([]);
  const sessionSelector = useCallback(store => store.userData, []);
  const [userDetails] = useSessionContext(sessionSelector);

  const { data, batchAuditLoading, error } = useSubscription(FETCH_BATCH_AUDIT, {
    variables: { batchId: jobId },
    skip: !visible || (contentType !== ApprovalModal),
    shouldResubscribe: true,
  });

  useEffect(() => {
    if(data) {
      setTimelineLoader(true);
      const timelineData = batchAuditData(data?.batchAudit?.batchAuditList, users, t);
      setAuditData(timelineData);
      setTimelineLoader(false);
    }
  }, [data]);

  const onChangeCollapse = (key) => {
    if (documentChartRef?.current) {
      documentChartRef.current.chart.resetSeries();
    }
    if (emailChartRef?.current) {
      emailChartRef.current.chart.resetSeries();
    }
    if(!key) return;
    setActiveChannel(key);
    setTotalStats({success:0, inprogress:0, failure:0});
    setLanguageStats([]);
    setRenderSearchTable(false);
    setTimeout(() => {
      setRenderSearchTable(true);
    }, 200);
  };


  const processJobApproval = useCallback(
    async (reject, additionalPayload = {}, rejected = () => {}) => {
      setTimelineLoader(true);
      !reject && setLoading(true);
      const payload = Object.assign({}, {
        batchId: activeItem?.id,
      }, additionalPayload);
      try {
        const response = await handleApproval(payload);
        if (response && response.statusCode === 4004 && reject) {
          setRejectionModal(false);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setTimelineLoader(false);
        rejected();
        setLoading(false);
      }
    },
    [activeItem],
  );

  const handleSentPrint = async () =>{
    const payload = {
      search: selectedSearchFilters,
      printStatus:'yet_to_print',
      jobId: jobId,
      ...(!selectedAllRows && {transactionIds: selectedRowKeys.join(',')})
    }
    try {
      const res = await sendForPrinting(payload);
      if (res?.statusCode === 200) {
        message.success(t(trans.PRINT_INITIATED_SUCCESS_MESSAGE.key, trans.PRINT_INITIATED_SUCCESS_MESSAGE.value));
        onCancel('');
      } else {
        switch (res?.statusCode) {
          case 5105:
            return message.error(t(trans.META_SEARCH_EXECUTION_FAILED_ERROR.key, trans.META_SEARCH_EXECUTION_FAILED_ERROR.value));
          case 5108:
            return message.error(t(trans.RECORDS_NOT_AVAILABLE_ERROR.key, trans.RECORDS_NOT_AVAILABLE_ERROR.value));
          case 5109:
            return message.error(t(trans.PRINT_NOT_CONFIGURED_ERROR.key, trans.PRINT_NOT_CONFIGURED_ERROR.value));
          case 5110:
            return message.error(t(trans.PRINT_CONFIGURATION_INVALID_ERROR.key, trans.PRINT_CONFIGURATION_INVALID_ERROR.value));
          case 5111:
            return message.error(t(trans.UNABLE_TO_SEND_FOR_PRINTING_ERROR.key, trans.UNABLE_TO_SEND_FOR_PRINTING_ERROR.value));
          default:
            return null;
        }
      }
    } catch (error) {
      console.error('Error send for printing:', error);
    }
  }

  const unHighlightSeries =(value,key) =>{
    value = value.charAt(0).toUpperCase() + value.slice(1);
     if (activeChannel === 'Hybrid') return;
    const chartRef = activeChannel === 'Documents' ? documentChartRef.current : activeChannel === 'Email' ? emailChartRef.current : {};    
    if (key === keyNames.status) {
      chartRef.chart.toggleSeries(value);
    } else if (key === keyNames.language && activeChannel !== 'Email') {
      const barChartRef = documentBarChartRef.current;
      const dataPointIndex = barChartRef.chart.w.config.xaxis.categories.findIndex((v) => v === value);
      barChartRef.chart.toggleDataPointSelection(0, dataPointIndex);
    }
    else if (key === keyNames.language && activeChannel === 'Email') {
      const barChartRef = emailBarChartRef.current;
      const dataPointIndex = barChartRef.chart.w.config.xaxis.categories.findIndex((v) => v === value);
      barChartRef.chart.toggleDataPointSelection(0, dataPointIndex);
    }
  };

  useEffect(() => {
    setStatsFilter([]);
  }, [activeChannel])

  useEffect(() => {
    if(!visible) {
      setStatsFilter([]);
      setRemovedRecord(false);
      setFailureRecord(false);
    }
    if (visible) {
      channelStatsPayload.jobId = jobId
      channelStatsPayload.channel = activeChannel === 'Documents' ? 'print' : activeChannel.toLowerCase();
      const res = aggregateBatchStats(activeItem?.batchStats, activeChannel)
      setTotalStats({success: res.successCount, inprogress: res.inQueueCount, failure: res.failureCount});
    }
    return () => {
      const channelTotals = getChannelTotals(activeItem?.batchStats || []);
      if(!visible) setActiveChannel(channelTotals.print > 0 ? 'Documents' : channelTotals.email > 0 ? 'Email' : channelTotals.hybrid > 0 ? 'Hybrid' : 'Documents')
      setSelectedAllRows(true);
    };
  }, [visible, jobId, activeChannel]);

  const reloadSync = activePanel =>
    contentType === PrintingModal && (
      <Tooltip placement="right" title={t(trans.SYNC_STATS.key, trans.SYNC_STATS.value)}>
        <Icon type={'sync'} spin={sync} onClick={() => setSync(true)} />
      </Tooltip>
    )

  const staticStatsKeys = contentType !== PrintingModal ? [...STATIC_STATS_KEYS, 'status'] : STATIC_STATS_KEYS

  const panels = () => {
    if (activeItem?.batchType === 'HYBRID') return [];
    const panelObj = {
      t,
      activeItem,
      contentType,
      setStatsFilter,
      statsFilter,
      documentChartRef,
      emailChartRef,
      documentBarChartRef,
      totalStats,
      languageStats,
      statsKeys:[keyNames.status, keyNames.language],
      staticStatsKeys: staticStatsKeys,
      activeChannel,
      setRemovedRecord,
      setFailureRecord,
      setExclusionHistory,
      selectedSearchFilters,
      emailBarChartRef,
      sync,
      setSync
    };
    return panelData(panelObj);
  };

  return (
    <ReusableModal
      visible={visible}
      title={title}
      onCancel={() => onCancel(false)}
      width="90%"
      styleProps={{
        top: 'auto',
        maxWidth: '90%',
        margin: '0 auto',
      }}
      bodyStyleProps={{
        padding: '20px',
        overflow: 'auto',
      }}
      afterClose={() => setRemovedRecord(false)}
      footer={
        <ButtonContainer ref={buttonContainerRef} visibility={removedRecord || failureRecord}>
          {contentType === ApprovalModal && getJobStatus(activeItem?.status, userDetails?.roles) && (
            <>
              <ActionButton
                medium={window.innerWidth > 1440}
                small={window.innerWidth <= 1440}
                label={t(
                  trans.APPROVE_BATCH_BUTTON.key,
                  trans.APPROVE_BATCH_BUTTON.value,
                )}
                onClick={() => processJobApproval(false, { approvalType: 'approve' })}
                loading={loading}
                enableShadow
              />
              <ActionButton
                medium={window.innerWidth > 1440}
                small={window.innerWidth <= 1440}
                label={t(trans.REJECT_BATCH_BUTTON.key, trans.REJECT_BATCH_BUTTON.value)}
                backgroundColor={'danger'}
                fontColor={'secondary'}
                onClick={() => {
                  setRejectionModal(true);
                }}
                enableShadow
              />
            </>
          )}
          {contentType === PrintingModal && (
            <ActionButton
              medium={window.innerWidth > 1440}
              small={window.innerWidth <= 1440}
              label={t(trans.SEND_FOR_PRINTNG_BUTTON.key, trans.SEND_FOR_PRINTNG_BUTTON.value)}
              enableShadow
              onClick={handleSentPrint}
              disabled={selectedRowKeys.length === 0}
            />
          )}
        </ButtonContainer>
      }
      centered
      maskClosable={true}
      className="approvalModal right-aligned-modal"
      transitionName={'ant-modal-slide-in'}
    >
      <Layout style={{ height: 'auto', backgroundColor: 'transparent' }}>
        <ApprovalContainer contentType={contentType}>
          {activeItem?.batchType !== 'HYBRID' && (
            <Sider className="leftSide">
              <ReusableDynamicCollapse
                activeKey={activeChannel}
                onChange={onChangeCollapse}
                defaultActiveKey={['Documents']}
                expandIcon="right"
                expandIconStyles={{ fontSize: '16px' }}
                styles={{
                  width: '100%',
                  backgroundColor: 'transparent',
                }}
                className="approval-collapse"
                panelClassName="approvla-collapse-panel"
                panelData={panels()}
                extra={reloadSync}
                channelTotals={getChannelTotals(activeItem?.batchStats || [])}
                isPrintingModal={contentType === PrintingModal}
              />
            </Sider>
          )}
          <Content className="RightSide" style={activeItem?.batchType === 'HYBRID' ? { width: '100%', maxWidth: '100%', flex: '1 1 100%', marginLeft: '20px' } : {}}>
            {contentType === ApprovalModal && (
              timelineLoader ?
                <Spin tip={t(trans.PLEASE_WAIT_TEXT.key, trans.PLEASE_WAIT_TEXT.value)} />
              :  auditData.length > 0 && <Timeline data={auditData} />
            )}
            <div
              className="table-wrapper"
              style={{ background: ' #f6f6f6', borderRadius: '24px', padding: '20px', overflow:"auto" }}
            >
              {renderSearchTable && (
                <SearchableTable
                  jobId={jobId}
                  visible={visible}
                  contentType={contentType}
                  defaultColumnCount={2}
                  statsFilter={statsFilter}
                  setStatsFilter={setStatsFilter}
                  statsKeys={[keyNames.status, keyNames.language]}
                  chartCallBack={unHighlightSeries}
                  staticStatsKeys={staticStatsKeys}
                  activeChannel={activeChannel}
                  removedRecord={removedRecord}
                  failureRecord={failureRecord}
                  setRemovedRecord={setRemovedRecord}
                  setFailureRecord= {setFailureRecord}
                  exclusionHistory={exclusionHistory}
                  selectedRowKeys = {selectedRowKeys}
                  setSelectedRowKeys = {setSelectedRowKeys}
                  selectedAllRows={selectedAllRows}
                  setSelectedAllRows={setSelectedAllRows}
                  setSelectedSearchFilters={setSelectedSearchFilters}
                />
              )}
            </div>
          </Content>
        </ApprovalContainer>
        <BatchRejectionModal
          visible={rejectionModal}
          setRejectionModal={setRejectionModal}
          processJobApproval={processJobApproval}
          buttonContainerRef={buttonContainerRef} />
      </Layout>
    </ReusableModal>
  );
};

export default withTranslation(NAMESPACES)(ApprovalDashboardModal);
